import { GameSettingBaseProps } from "components/common/GameSettingModal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

function WTBGameSettingBody({
  setGameSetting,
  roomParams: gameParams,
}: GameSettingBaseProps) {
  const { t, i18n } = useTranslation();
  const [rounds, setRounds] = useState(5);
  const [botCount, setBotCount] = useState(1);
  const [answerTime, setAnswerTime] = useState(10);
  const [votingTime, setVotingTime] = useState(15);
  const [language, setLanguage] = useState<"ko" | "en">(
    i18n.language === "en" ? "en" : "ko"
  );

  const handleLanguageChange = () => {
    const newLang = language === "ko" ? "en" : "ko";
    setLanguage(newLang);
    setGameSetting({
      rounds,
      botCount,
      answerTime,
      votingTime,
      language: newLang,
    });
  };

  const handleBotCount = (add: boolean) => {
    const newBotCount = add ? botCount + 1 : botCount - 1;
    if (newBotCount >= 1 && newBotCount <= 5) {
      setBotCount(newBotCount);
      setGameSetting({
        rounds,
        botCount: newBotCount,
        answerTime,
        votingTime,
        language,
      });
    }
  };

  const handleRounds = (add: boolean) => {
    const newRounds = add ? rounds + 1 : rounds - 1;
    if (newRounds >= 3 && newRounds <= 10) {
      setRounds(newRounds);
      setGameSetting({
        rounds: newRounds,
        botCount,
        answerTime,
        votingTime,
        language,
      });
    }
  };

  const handleAnswerTime = (add: boolean) => {
    const newTime = add ? answerTime + 5 : answerTime - 5;
    if (newTime >= 10 && newTime <= 20) {
      setAnswerTime(newTime);
      setGameSetting({
        rounds,
        botCount,
        answerTime: newTime,
        votingTime,
        language,
      });
    }
  };

  const handleVotingTime = (add: boolean) => {
    const newTime = add ? votingTime + 5 : votingTime - 5;
    if (newTime >= 10 && newTime <= 30) {
      setVotingTime(newTime);
      setGameSetting({
        rounds,
        botCount,
        answerTime,
        votingTime: newTime,
        language,
      });
    }
  };

  useEffect(() => {
    const initialLanguage = i18n.language === "en" ? "en" : "ko";
    setGameSetting({
      rounds: 5,
      botCount: 1,
      answerTime: 10,
      votingTime: 15,
      language: initialLanguage,
    }); // 기본값 설정
  }, [setGameSetting, i18n.language]);

  const renderTimeSetting = (
    label: string,
    value: number,
    onDecrease: () => void,
    onIncrease: () => void,
    min: number,
    max: number
  ) => (
    <div className="flex items-center gap-4">
      <div className="text-gray-200">{label}</div>
      <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
        <button
          className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-l-lg transition-colors"
          onClick={onDecrease}
          disabled={value <= min}
        >
          –
        </button>
        <span className="text-white min-w-[20px] text-center">{value}s</span>
        <button
          className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-r-lg transition-colors"
          onClick={onIncrease}
          disabled={value >= max}
        >
          +
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-4 flex flex-col items-center justify-center gap-8">
      {/* 언어 설정 */}
      <div className="flex items-center gap-4">
        <div className="text-gray-200">{t("WTB.language")}</div>
        <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
          <button
            className={`px-4 py-1 text-white text-sm hover:bg-gray-600 rounded-l-lg transition-colors ${
              language === "ko" ? "bg-indigo-600" : ""
            }`}
            onClick={() => handleLanguageChange()}
          >
            한국어
          </button>
          <button
            className={`px-4 py-1 text-white text-sm hover:bg-gray-600 rounded-r-lg transition-colors ${
              language === "en" ? "bg-indigo-600" : ""
            }`}
            onClick={() => handleLanguageChange()}
          >
            English
          </button>
        </div>
      </div>

      {/* 봇 수 설정 */}
      <div className="flex items-center gap-4">
        <div className="text-gray-200">{t("WTB.botCount")}</div>
        <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
          <button
            className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-l-lg transition-colors"
            onClick={() => handleBotCount(false)}
            disabled={botCount <= 1}
          >
            –
          </button>
          <span className="text-white min-w-[20px] text-center">
            {botCount}
          </span>
          <button
            className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-r-lg transition-colors"
            onClick={() => handleBotCount(true)}
            disabled={botCount >= 5}
          >
            +
          </button>
        </div>
      </div>

      {/* 라운드 수 설정 */}
      <div className="flex items-center gap-4">
        <div className="text-gray-200">{t("round")}</div>
        <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
          <button
            className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-l-lg transition-colors"
            onClick={() => handleRounds(false)}
            disabled={rounds <= 3}
          >
            –
          </button>
          <span className="text-white min-w-[20px] text-center">{rounds}</span>
          <button
            className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-r-lg transition-colors"
            onClick={() => handleRounds(true)}
            disabled={rounds >= 10}
          >
            +
          </button>
        </div>
      </div>

      {/* 답변 시간 설정 */}
      {renderTimeSetting(
        t("WTB.answerTime"),
        answerTime,
        () => handleAnswerTime(false),
        () => handleAnswerTime(true),
        10,
        20
      )}

      {/* 투표 시간 설정 */}
      {renderTimeSetting(
        t("WTB.votingTime"),
        votingTime,
        () => handleVotingTime(false),
        () => handleVotingTime(true),
        10,
        30
      )}
    </div>
  );
}

export default WTBGameSettingBody;
